/* Copyright (c) 2017 Backpack Health LLC */
/* global document, IS_ANDROID_BUILD, cordova */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import * as _ from 'lodash';

import UrlUtil from '../../utilities/UrlUtil';

const ExternalLink = (props) => (
  <a target={(props.openInCurrentWindow ? '' : '_blank')} href={props.href} rel="noopener noreferrer" onClick={props.onClick} className={props.className} style={props.style}>
    {props.cutProtocols ? UrlUtil.cutProtocols(props.children) : props.children}
  </a>
);
ExternalLink.propTypes = {
  children: PropTypes.any.isRequired,
  href: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  openInCurrentWindow: PropTypes.bool,
  cutProtocols: PropTypes.bool,
};
ExternalLink.defaultProps = {
  onClick: () => {},
  openInCurrentWindow: false,
};


const ExternalOrInBrowserlLink = (props) => {
  const { className, style, url, onClick, children } = props;

  if (IS_ANDROID_BUILD) {
    return (
      <TextLink
        className={className}
        style={style}
        onClick={() => {
          cordova.InAppBrowser.open(url, '_blank', 'location=yes');
          if (onClick) { onClick(); }
        }}
      >
        {children}
      </TextLink>
    );
  } else {
    return (
      <ExternalLink href={url} onClick={onClick} className={className} style={style}>
        {children}
      </ExternalLink>
    );
  }
};
ExternalOrInBrowserlLink.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  url: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.any,
};

const MailToLink = (props) => {
  const classes = classnames(props.className, {
    'font-color-error': props.action === 'destructive',
    'font-color-profile': props.action === 'profile',
  });
  return (
    <a className={classes} href={`mailto:${props.email}`}>
      {props.cutProtocols ? UrlUtil.cutProtocols(props.children) : props.children}
    </a>
  );
};
MailToLink.propTypes = {
  children: PropTypes.any.isRequired,
  email: PropTypes.string.isRequired,
  action: PropTypes.oneOf(['default', 'destructive', 'profile']),
  className: PropTypes.string,
  cutProtocols: PropTypes.bool,
};

const TextLink = (props) => {
  const classes = classnames(
    'cmp-text-link',
    props.colorClass,
    props.className,
    {
      'font-color-error': props.action === 'destructive',
      'font-color-profile': props.action === 'profile',
      'is-underlined': props.isUnderlined,
    }
  );
  const styles = _.assign({
    color: props.color ? `rgb(${props.color.r}, ${props.color.g}, ${props.color.b})` : null,
  }, props.style);
  if (props.isDisabled) {
    styles.cursor = 'default';
    styles.opacity = 0.6;
  }
  return (
    <a className={classes} onClick={props.onClick} style={styles}>
      {props.cutProtocols ? UrlUtil.cutProtocols(props.children) : props.children}
    </a>
  );
};
TextLink.propTypes = {
  children: PropTypes.any.isRequired,
  isUnderlined: PropTypes.bool,
  colorClass: PropTypes.string,
  className: PropTypes.string,
  action: PropTypes.oneOf(['default', 'destructive', 'profile']),
  onClick: PropTypes.func,
  color: PropTypes.shape({
    r: PropTypes.number.isRequired,
    g: PropTypes.number.isRequired,
    b: PropTypes.number.isRequired,
  }),
  style: PropTypes.object,
  cutProtocols: PropTypes.bool,
  isDisabled: PropTypes.bool,
  href: PropTypes.string,
};
TextLink.defaultProps = {
  onClick: () => {},
  action: 'default',
};


class CopyLink extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      copied: false,
    };
  }

  copyLinkToClipboard(copyText) {
    this.setState({ copied: true });

    const el = document.createElement('textarea');
    el.value = copyText;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    setTimeout(() => this.setState({ copied: false }), 5000);
  }

  render() {
    const { children, copiedTextId } = this.props;

    const copied = (
      <span style={{ fontStyle: 'italic' }} className="font-color-middle-gray">
        <FormattedMessage id={copiedTextId} />
      </span>
    );

    const label = copiedTextId && this.state.copied ? copied : children;

    return (
      <TextLink {...this.props} onClick={() => { this.copyLinkToClipboard(this.props.copyText); }}>
        { label }
      </TextLink>
    );
  }
}
CopyLink.propTypes = {
  children: PropTypes.any,
  copyText: PropTypes.string,
  copiedTextId: PropTypes.string,
};

export {
  ExternalLink,
  ExternalOrInBrowserlLink,
  TextLink,
  MailToLink,
  CopyLink,
};
